import axios from 'axios'
import store from '@/store'
import JobDefinitionAPI from './job_definition'
import EnablerAPI from '@/services/api/node/enabler'
import AuthActions from '@/store/core/auth/actions-types'
import { isTokenNearExpiry } from '@/store/core/auth/token-utils'

export default {
  async getEnabler(job) {
    if (!job.job_definition_id) {
      console.log('[AI asset association] Skipped: job definition ID is missing')
      return null
    }

    const definition = await JobDefinitionAPI.getJobDefinition(job.job_definition_id)
    const nodeId = definition.data.node_id || definition.data.job_definition_id || definition.data.id
    if (!nodeId) throw new Error('Job definition node ID is missing')

    const response = await EnablerAPI.getEnablersByNode(nodeId, {
      'filter[type]': 'ai_asset_association',
      limit: 1000
    })
    const enablers = response.data.items || []
    return enablers.find(item => item.enabler_type === 'ai_asset_association') || null
  },

  async suggest(job, files) {
    const enabler = await this.getEnabler(job)
    if (!enabler) {
      console.log('[AI asset association] Skipped: no association enabler configured')
      return []
    }

    const rawParams = enabler.params ?? enabler.enabler_params
    const params = typeof rawParams === 'string' ? JSON.parse(rawParams) : rawParams
    if (!params || typeof params.n8n_url !== 'string' || !params.n8n_url.trim()) {
      throw new Error('AI asset association URL is missing')
    }

    if (store.state.core.auth.token && isTokenNearExpiry(store.state.core.auth.token)) {
      await store.dispatch('core/auth/' + AuthActions.RENEW_TOKEN)
    }
    const contentUrl = store.getters['app/getSelectedContentAppAccessUrl']()
    const result = await axios.post(params.n8n_url, {
      job_id: job.job_id,
      job_url: store.getters['app/getSelectedJobAppAccessUrl']() || '',
      content_url: typeof contentUrl === 'string' ? contentUrl.replace(/\/?job\/?$/, '') : '',
      node_url: process.env.VUE_APP_NODE_API_URL || '',
      core_url: process.env.VUE_APP_CORE_API_URL || '',
      files
    }, {
      headers: { 'Content-Type': 'application/json', token: store.state.core.auth.token },
      timeout: 60000
    })
    // Accept a single classification object, batches, or the original flat list.
    const batches = Array.isArray(result.data) ? result.data : [result.data]
    if (!Array.isArray(result.data) && !Array.isArray(result.data?.classification)) {
      throw new Error('Invalid AI asset association response')
    }
    const suggestions = batches.flatMap(item => {
      if (item && Object.prototype.hasOwnProperty.call(item, 'classification')) {
        if (!Array.isArray(item.classification)) throw new Error('Invalid AI asset association response')
        return item.classification
      }
      return [item]
    }).map(suggestion => {
      // An explicitly empty session means a new asset, not a version of an existing one.
      if (suggestion && suggestion.markup_session_id === '') {
        const normalizedSuggestion = {
          ...suggestion,
          version_of: '',
          file_state: 'new'
        }
        return normalizedSuggestion
      }
      return suggestion
    })
    return suggestions
  }
}

// Only resolve against the job's loaded formats/assets; AI titles are descriptive.
export function resolveAssetAssociation(suggestion, baseInstances) {
  if (!suggestion || typeof suggestion.file !== 'string' ||
      !['new', 'version'].includes(suggestion.file_state)) return null

  const formats = baseInstances.flatMap(base => base.formatInstances || [])
  const format = formats.find(item => String(item.format_instance_id) === String(suggestion.format_instance_id))
  if (suggestion.file_state === 'new') {
    return format
      ? { formatInstanceId: format.format_instance_id, assignedAssetId: null }
      : null
  }

  const hasSessionId = suggestion.markup_session_id != null
  if (!hasSessionId && (typeof suggestion.version_of !== 'string' || !suggestion.version_of)) return null
  // A supplied but invalid format ID must not silently resolve to another format.
  if (suggestion.format_instance_id != null && !format) return null
  const matches = (format ? [format] : formats).flatMap(item =>
    // A session identifies the asset across versions, whose filenames may differ.
    (item.assets || []).filter(asset => hasSessionId
      ? String(asset.markup_session_id) === String(suggestion.markup_session_id)
      : asset.fileName === suggestion.version_of)
      .map(asset => ({ formatInstanceId: item.format_instance_id, assignedAssetId: asset.job_deliver_asset_id }))
  )
  return matches.length === 1
    ? matches[0]
    : null
}
